import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import LabelIcon from "@mui/icons-material/Label";
import GroupIcon from "@mui/icons-material/Group";
import { useAuthApi } from "../hooks/useAuthApi";
import { MGMT } from "../env";
import { fmtDate, statusColor } from "../format";
import type { Driver, Label } from "../types";
import { fullDriversFromResponse, type DriverListResponse } from "../driverResponses";
import { useToast } from "../toast";
import ConfirmDialog from "../dialogs/ConfirmDialog";
import LabelDialog from "../dialogs/LabelDialog";
import DriverGroupsDialog from "../dialogs/DriverGroupsDialog";

export default function Drivers({ onOpenDetail }: { onOpenDetail?: (id: number) => void }) {
  const api = useAuthApi();
  const [items, setItems] = useState<Driver[]>([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState<Driver | null>(null);
  const [labelsFor, setLabelsFor] = useState<Driver | null>(null);
  const [labelItems, setLabelItems] = useState<Label[]>([]);
  const [addingLabelTo, setAddingLabelTo] = useState<Driver | null>(null);
  const [showGroups, setShowGroups] = useState(false);
  const [search, setSearch] = useState("");
  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api<DriverListResponse>(`${MGMT}/drivers`);
      setItems(fullDriversFromResponse(data));
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const loadLabels = useCallback(
    async (driver: Driver) => {
      try {
        const data = await api<Label[]>(`${MGMT}/drivers/${driver.id}/labels`);
        setLabelItems(data ?? []);
        setLabelsFor(driver);
      } catch (e) {
        toast.show((e as Error).message, "error");
      }
    },
    [toast]
  );

  const deleteLabel = async (driver: Driver, labelId: number) => {
    try {
      await api(`${MGMT}/drivers/${driver.id}/labels/${labelId}`, { method: "DELETE" });
      toast.show("Label removed");
      loadLabels(driver);
    } catch (e) {
      toast.show((e as Error).message, "error");
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await api(`${MGMT}/drivers/${deleting.id}`, { method: "DELETE" });
      toast.show("Driver deleted");
      setDeleting(null);
      load();
    } catch (e) {
      toast.show((e as Error).message, "error");
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((d) => {
      const groupNames = (d.groups ?? []).map((g) => g.name).join(" ");
      const haystack = [d.name, d.email, d.phone, d.status, groupNames].filter(Boolean).join(" ").toLowerCase();
      return haystack.includes(q);
    });
  }, [items, search]);

  const hasFilters = search.trim().length > 0;

  return (
    <>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={<GroupIcon />}
          onClick={() => setShowGroups(true)}
        >
          Manage Groups
        </Button>
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Stack>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 2 }} alignItems="center">
        <TextField
          size="small"
          placeholder="Filter drivers…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260 }}
          InputProps={{
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch("")}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        {hasFilters && <Button size="small" onClick={() => setSearch("")}>Clear filters</Button>}
      </Stack>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Groups</TableCell>
              <TableCell>Labels</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}
            {!loading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Box sx={{ py: 3 }}>
                    <Typography color="text.secondary">
                      {hasFilters ? "No drivers match the current filters." : "No drivers yet. Add your first driver."}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {filtered.map((d) => (
              <TableRow
                key={d.id}
                hover
                sx={{ cursor: onOpenDetail ? "pointer" : undefined }}
                onClick={(e) => {
                  // Don't navigate if clicking on action buttons
                  if ((e.target as HTMLElement).closest("button")) return;
                  onOpenDetail?.(d.id);
                }}
              >
                <TableCell sx={{ fontWeight: 600 }}>{d.name}</TableCell>
                <TableCell sx={{ color: "text.secondary" }}>{d.email}</TableCell>
                <TableCell sx={{ color: "text.secondary" }}>
                  {d.phone ?? "–"}
                </TableCell>
                <TableCell>
                  <Chip label={d.status} color={statusColor(d.status)} size="small" />
                </TableCell>
                <TableCell>
                  {(d.groups ?? []).length === 0 ? (
                    <Typography variant="body2" color="text.secondary">–</Typography>
                  ) : (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                      {(d.groups ?? []).map((g) => (
                        <Chip
                          key={g.id}
                          label={g.name}
                          size="small"
                          color={g.is_dynamic ? "info" : "default"}
                          variant="outlined"
                          title={g.is_dynamic ? "Dynamic (via label rules)" : "Explicit member"}
                        />
                      ))}
                    </Stack>
                  )}
                </TableCell>
                <TableCell>
                  {labelsFor?.id === d.id && labelItems.length > 0 ? (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                      {labelItems.map((lbl) => (
                        <Chip
                          key={lbl.id}
                          label={`${lbl.key}: ${lbl.value}`}
                          size="small"
                          variant="outlined"
                          onDelete={() => deleteLabel(d, lbl.id)}
                        />
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {labelsFor?.id === d.id ? "No labels" : "–"}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{fmtDate(d.created_at)}</TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    title="Manage labels"
                    onClick={() => {
                      setAddingLabelTo(d);
                      loadLabels(d);
                    }}
                  >
                    <LabelIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setDeleting(d)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <LabelDialog
        open={addingLabelTo !== null}
        entityType="driver"
        entityId={addingLabelTo?.id ?? 0}
        onClose={() => setAddingLabelTo(null)}
        onSaved={() => {
          if (addingLabelTo) loadLabels(addingLabelTo);
          setAddingLabelTo(null);
        }}
      />

      <DriverGroupsDialog open={showGroups} onClose={() => setShowGroups(false)} />

      <ConfirmDialog
        open={deleting !== null}
        title="Delete driver?"
        message={`Are you sure you want to delete "${deleting?.name}"? This cannot be undone.`}
        onCancel={() => setDeleting(null)}
        onConfirm={handleDelete}
      />
    </>
  );
}
